import React, { useEffect, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import { createNotification, deleteNotification, fetchNotifications, NotificationRow, updateNotification } from "./api";
import ActionToast, { ToastAction } from "./ActionToast";

const NotificationsPage: React.FC = () => {
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);

  const loadNotifications = async () => {
    try {
      const data = await fetchNotifications();
      setRows(data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch notifications");
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    if (!toastAction) return;
    const timer = setTimeout(() => setToastAction(null), 6500);
    return () => clearTimeout(timer);
  }, [toastAction]);

  const openAddModal = () => {
    setEditingId(null);
    setMessage("");
    setShowModal(true);
  };

  const openEditModal = (row: NotificationRow) => {
    setEditingId(row.id);
    setMessage(row.message || "");
    setShowModal(true);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      alert("Message is required.");
      return;
    }

    setBusy(true);
    try {
      if (editingId === null) {
        await createNotification(message.trim());
      } else {
        await updateNotification(editingId, message.trim());
      }
      setShowModal(false);
      setMessage("");
      setEditingId(null);
      await loadNotifications();
      setToastAction("saved");
    } catch (err: any) {
      alert(err.message || "Failed to save notification");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: number) => {
    if (!window.confirm("Delete this notification?")) return;
    try {
      await deleteNotification(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      setToastAction("deleted");
    } catch (err: any) {
      alert(err.message || "Failed to delete notification");
    }
  };

  return (
    <DashboardLayout>
      <ActionToast action={toastAction} />
      <h1 className="list-title">Notifications</h1>
      <p className="list-subtitle">{rows.length} total notifications</p>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="list-toolbar mb-3">
        <button type="button" className="btn btn-success" onClick={openAddModal}>
          <i className="fa-solid fa-plus me-2" />
          Add Notification
        </button>
      </div>
      <div className="table-shell table-responsive">
        <table className="table data-table mb-0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Message</th>
              <th>Created</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{row.message}</td>
                <td>{row.createdAt || "-"}</td>
                <td className="text-end">
                  <div className="action-icons justify-content-end">
                    <button type="button" className="icon-btn" title="Edit" onClick={() => openEditModal(row)}>
                      <i className="fa-regular fa-pen-to-square" />
                    </button>
                    <button type="button" className="icon-btn icon-btn-danger" title="Delete" onClick={() => onDelete(row.id)}>
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-4 text-muted">
                  No notifications found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="dash-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h5 className="dash-modal-title">{editingId === null ? "Add Notification" : "Edit Notification"}</h5>
              <button type="button" className="dash-modal-close" onClick={() => setShowModal(false)}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <form onSubmit={onSubmit}>
              <div className="dash-modal-body">
                <label className="form-label">Message</label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Enter notification message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              <div className="dash-modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={busy}>
                  {busy ? "Saving..." : editingId === null ? "Add Notification" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default NotificationsPage;
